import React from 'react';
import type { Agent } from '../types';

const NAV_LINKS = [
  { label: 'About', href: 'https://www.skyluxtravel.com/about-us' },
  { label: 'Reviews', href: 'https://www.skyluxtravel.com/reviews' },
  { label: 'Privacy Policy', href: 'https://www.skyluxtravel.com/privacy' },
  { label: 'Terms & Conditions', href: 'https://www.skyluxtravel.com/terms' },
] as const;

const COMPANY_PHONE = '855 - 975 - 9269';
const COMPANY_PHONE_TEL = '+18559759269';

const FLAG_IMG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABYAAAAOCAMAAAAc7xz/AAAAAXNSR0IB2cksfwAAAXdQTFRFAAAAHkJ6FTp0GTx3EzdzHD54EjdyIUN7EjZyH0J6Djh0Ikh/lA451g8xwA80vw8zIj94NFOHLE2CLk+DLU6DL0+DM1OGMVKFJkqAI0B5wLbI/Obp9+DlFTRwMFGFJkh+K0yCLE2DKUuBKUqAJ0+EFjl1oTpf4ENdzkRhzUNhFjZxL1CEKkuBKUuAJEiAGDdzu52z+MXM7r/J7b/IJUF6Lk6DKEqAK0yBIkmBJ0h/pU9x6GJ41mF61mF5FDNwJEZ9I0V9KkyCJEqAFjVxs36Z8pup5Jio5JioABleCjBuBClpBitqBCppBixrBy5sBi1rACpqAB1hpGGB7H+R3X2R3HyQirnTe6rIgK/LeqnHgrHMeKjGhrTPd6fGhbPOc6nIir7XwXeQ6nCD2nGH2XGH+pml/Jun+5un/Jyn+pqm/Zyo+Zmm/Zyn+Zil7qSx5KSz56Sy56Ox1FZw1Fdw01dx01Zx8MjR8cnRyzpZzDtZ+OTpwRY6whY6qedTmwAAAH10Uk5TAOTx8fHx8fHx8fHx8fHx5P/////////////////y///////////////y8v//////////8vL///////////Ly///////////y8v////////////////Ly//////////////////Ly///////////////y8v//8vL/8v//5PFgKX2WAAAAwUlEQVR4nGNgZGJmYWVj5+Dk4ubh5YMCfgYBQSFhEVExYXEJSSlpGRhgkJWTV5BXVBJRVlFVU9eAAk0GLW15HXkFXQVdPX0DQyMoMGYwMdU1MzeTM9O2sLSytoECWwY7YXszB3NLR3knZxdXNyhwZ/Dw9PL28fXz9Q8IDAoOgYJQhrDwiMio6JjYuPiExKRkKEhhSE1Lz8jMys7KzsnNyy+AgkKGomI4KIGDUoayciygjKGiEguoYKjCChiqa7CAagCuAF/55ytfQAAAAABJRU5ErkJggg==';

export type BookingSiteHeaderProps = {
  agent: Agent;
  logoSrc?: string;
  /** Logo link — production uses current booking URL. */
  homeHref?: string;
};

function agentShortName(agent: Agent): string {
  const nick = agent.nickname?.trim();
  if (nick) return nick;
  const parts = agent.name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]} ${parts[1]![0]}.`;
  return parts[0] ?? 'Agent';
}

function LangChevronIcon() {
  return (
    <svg className="booking-header__lang-chevron" viewBox="0 0 24 24" aria-hidden>
      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg className="booking-header__phone-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
      />
    </svg>
  );
}

export default function BookingSiteHeader({
  agent,
  logoSrc = `https://www.skyluxtravel.com/static/img/header/logo-dark-new.png`,
  homeHref = 'https://www.skyluxtravel.com/',
}: BookingSiteHeaderProps) {
  const agentEmail = agent.email?.trim() || 'support@skyluxtravel.com';
  const photo = agent.avatar?.trim();
  const displayName = agentShortName(agent);

  return (
    <header className="booking-header">
      <div className="booking-header__container">
        <div className="booking-header__toolbar">
          <a className="booking-header__logo-link" href={homeHref}>
            <img className="booking-header__logo" src={logoSrc} alt="logo" />
          </a>

          <div className="booking-header__nav-cluster">
            {NAV_LINKS.map((item) => (
              <a
                key={item.href}
                className="booking-header__nav-link"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.label}
              </a>
            ))}
            <button type="button" className="booking-header__lang" aria-label="Language English">
              <img src={FLAG_IMG} alt="EN" width={22} height={14} />
              <span className="booking-header__lang-code">EN</span>
              <LangChevronIcon />
            </button>
          </div>

          <div className="booking-header__agent">
            <div className="booking-header__agent-contact">
              <div className="booking-header__agent-line">
                <a className="booking-header__agent-email" href={`mailto:${agentEmail}`}>
                  {agentEmail}
                </a>
              </div>
              <div className="booking-header__agent-line">
                <a className="booking-header__agent-phone" href={`tel:${COMPANY_PHONE_TEL}`}>
                  <PhoneIcon />
                  {COMPANY_PHONE}
                </a>
              </div>
            </div>

            <div className="booking-header__agent-names">
              <div className="booking-header__agent-names-desktop">
                <div className="booking-header__agent-label">Travel agent</div>
                <div className="booking-header__agent-name">{displayName}</div>
              </div>
              <div className="booking-header__agent-names-mobile">
                <div className="booking-header__agent-name">{displayName}</div>
              </div>
            </div>

            <div className="booking-header__agent-photo-wrap">
              {photo ? (
                <img className="booking-header__agent-photo" src={photo} alt={displayName} />
              ) : (
                <div className="booking-header__agent-photo booking-header__agent-photo--placeholder" aria-hidden />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
